/**
 * DD Chat Hook
 *
 * React hook for managing DD Chat sessions.
 * Uses strategy pattern: MockAssistant in mock mode, real API in demo/production.
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  sendDDChatMessage,
  type DDChatMessage,
  type DDStartupContext,
  type DDChatRequest,
} from '@/lib/supabase/edge-functions';
import { mode } from '@/lib/config/app-mode';
import { createDDAssistant } from './assistant-factory';
import { reportError } from '@/lib/errors';
import type { IDDAssistant, DDAssistantContext } from './assistant.types';
import type { StartupProfile } from '@/lib/state/profile-store';

// =============================================================================
// Types
// =============================================================================

export interface UseDDChatOptions {
  /** Initial startup context */
  startupContext?: DDStartupContext;
  /** Callback when message sent successfully */
  onMessageSent?: (message: DDChatMessage) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
  /** Investor preferences for contextual responses */
  investorPreferences?: {
    focusAreas?: string[];
    riskTolerance?: 'low' | 'medium' | 'high';
    investmentStage?: string[];
  };
  /** Investor ID for conversation tracking */
  investorId?: string;
  /** Startup ID */
  startupId?: string;
}

export interface UseDDChatReturn {
  /** Current message history */
  messages: DDChatMessage[];
  /** Whether a message is being processed */
  isLoading: boolean;
  /** Last error */
  error: Error | null;
  /** Send a message */
  sendMessage: (content: string) => void;
  /** Clear conversation history */
  clearHistory: () => void;
  /** Update startup context */
  setStartupContext: (context: DDStartupContext) => void;
  /** Current startup context */
  startupContext: DDStartupContext | null;
  /** Suggested follow-up questions */
  suggestedQuestions: string[];
  /** Topics discussed in conversation */
  topicsDiscussed: string[];
  /** Total tokens used in session */
  sessionTokens: number;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Generate a unique message ID
 */
function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Convert StartupProfile to DDStartupContext
 */
export function profileToDDContext(profile: StartupProfile): DDStartupContext {
  // Convert array fields to string format for chat context
  const competitorsString = profile.competitors?.join(', ');
  const previousVenturesString = profile.previousVentures?.join(', ');
  const marketSizeString = profile.marketSize
    ? `$${(profile.marketSize / 1000000).toFixed(1)}M`
    : undefined;

  // Convert fundraising history to summary string
  const fundraisingHistoryString = profile.fundraisingHistory?.length
    ? profile.fundraisingHistory
        .map((r) => `${r.stage || 'Round'}: $${(r.amount / 1000000).toFixed(1)}M (${r.date || 'N/A'})`)
        .join('; ')
    : undefined;

  // Convert Swiss verification to simple format
  const swissVerification = profile.swissVerification?.zefix
    ? {
        isSwissStartup: true,
        registrationStatus: profile.swissVerification.zefix.status || 'verified',
      }
    : undefined;

  // Convert Startupticker data to simple format
  const startuptickerData = profile.startuptickerData?.profile
    ? {
        featured: true,
        category: profile.startuptickerData.profile.sector,
      }
    : undefined;

  return {
    name: profile.name || 'Unnamed Startup',
    stage: profile.stage,
    industry: profile.industry,
    location: profile.location,
    foundedYear: profile.foundedYear,
    website: profile.website,
    teamSize: profile.teamSize,
    isSerialFounder: profile.isSerialFounder,
    previousVentures: previousVenturesString,
    tagline: profile.tagline,
    description: profile.description,
    problem: profile.problem,
    solution: profile.solution,
    targetCustomer: profile.targetCustomer,
    monthlyRevenue: profile.monthlyRevenue,
    userCount: profile.userCount,
    growthRate: profile.growthRate,
    marketSize: marketSizeString,
    competitors: competitorsString,
    fundingTarget: profile.fundingTarget,
    minimumTicket: profile.minimumTicket,
    isActivelyRaising: profile.isActivelyRaising,
    fundraisingHistory: fundraisingHistoryString,
    swissVerification,
    startuptickerData,
    dataRoomConnected: profile.dataRoomConnected,
    dataRoomProvider: profile.dataRoomProvider,
  };
}

/**
 * Add score data to context if available
 */
export function addScoresToContext(
  context: DDStartupContext,
  scores: {
    finalScore?: number;
    readinessLevel?: string;
    dimensions?: Array<{
      dimension: string;
      name: string;
      score: number;
      feedback?: string;
    }>;
    topRisks?: string[];
    topStrengths?: string[];
  }
): DDStartupContext {
  return {
    ...context,
    scores,
  };
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook for DD Chat functionality
 *
 * In MOCK mode: Uses MockAssistant with simulated responses
 * In DEMO/PRODUCTION mode: Calls real edge function API
 */
export function useDDChat(options: UseDDChatOptions = {}): UseDDChatReturn {
  const [messages, setMessages] = useState<DDChatMessage[]>([]);
  const [startupContext, setStartupContext] = useState<DDStartupContext | null>(
    options.startupContext || null
  );
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [topicsDiscussed, setTopicsDiscussed] = useState<string[]>([]);
  const [sessionTokens, setSessionTokens] = useState(0);

  // Create assistant instance (MockAssistant in mock mode)
  const assistantRef = useRef<IDDAssistant | null>(null);
  if (!assistantRef.current) {
    assistantRef.current = createDDAssistant();
  }

  // Mutation for sending messages
  const { mutate: chatMutate, reset: chatReset, isPending: chatIsPending, error: chatError } = useMutation({
    mutationFn: async (content: string) => {
      // When no startup context is set, use a minimal "general" context
      // so the assistant can still answer general investment questions.
      // In this mode, RAG retrieval will find no documents (no startup_id)
      // and fall back to general knowledge — which is the desired behavior.
      const effectiveContext: DDStartupContext = startupContext ?? {
        name: 'General',
        stage: 'N/A',
        industry: 'N/A',
      };

      // Create user message
      const userMessage: DDChatMessage = {
        id: generateMessageId(),
        role: 'user',
        content,
        timestamp: Date.now(),
      };

      // Optimistically add user message
      setMessages((prev) => [...prev, userMessage]);

      const assistant = assistantRef.current!;
      const assistantMode = assistant.getMode();

      // MOCK or RAG MODE: Use the assistant directly
      if (mode.isMock || assistantMode === 'rag') {
        const context: DDAssistantContext = {
          message: content,
          startupId: options.startupId || (startupContext ? 'mock-startup' : ''),
          investorId: options.investorId || 'mock-investor',
          startupContext: effectiveContext,
          history: messages,
        };

        const response = await assistant.chat(context);

        return {
          assistantMessage: {
            ...response.message,
            metadata: {
              ...response.message.metadata,
              richSources: response.sources || [],
            },
          },
          suggestedQuestions: response.suggestedQuestions || [],
          topicsDiscussed: response.topicsDiscussed || [],
          usage: response.usage,
        };
      }

      // MVP MODE: Call edge function API directly
      const request: DDChatRequest = {
        message: content,
        history: messages,
        startupId: options.startupId, // B2: server rebuilds context + gates on this
        startupContext: effectiveContext, // stripped before server send (SEC-DOC-03)
        investorPreferences: options.investorPreferences,
      };

      const response = await sendDDChatMessage(request);

      if (response.error || !response.data?.success || !response.data.data) {
        throw new Error(response.error || response.data?.error || 'Failed to get response');
      }

      return {
        assistantMessage: response.data.data.message,
        suggestedQuestions: response.data.data.suggestedQuestions || [],
        topicsDiscussed: response.data.data.topicsDiscussed || [],
        usage: response.data.usage,
      };
    },
    onSuccess: (result) => {
      // Add assistant message to history
      setMessages((prev) => [...prev, result.assistantMessage]);

      // Update suggested questions
      setSuggestedQuestions(result.suggestedQuestions);

      // Merge topics discussed
      setTopicsDiscussed((prev) => {
        const combined = new Set([...prev, ...result.topicsDiscussed]);
        return Array.from(combined);
      });

      // Update token count
      if (result.usage) {
        setSessionTokens((prev) => prev + result.usage!.totalTokens);
      }

      // Call success callback
      options.onMessageSent?.(result.assistantMessage);
    },
    onError: (error) => {
      // Keep user message visible but add an error notice so the user sees what happened
      const errorMessage: DDChatMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your message. Please try again.',
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMessage]);

      reportError(error, { context: 'useDDChat.sendMessage', level: 'warning' });
      options.onError?.(error instanceof Error ? error : new Error(String(error)));
    },
  });

  // Send message handler
  const sendMessage = useCallback(
    (content: string) => {
      if (!content.trim()) return;
      chatMutate(content.trim());
    },
    [chatMutate]
  );

  // Clear history handler
  const clearHistory = useCallback(() => {
    setMessages([]);
    setSuggestedQuestions([]);
    setTopicsDiscussed([]);
    setSessionTokens(0);
    chatReset();
  }, [chatReset]);

  // Update context handler
  const updateContext = useCallback((context: DDStartupContext) => {
    setStartupContext(context);
  }, []);

  return {
    messages,
    isLoading: chatIsPending,
    error: chatError,
    sendMessage,
    clearHistory,
    setStartupContext: updateContext,
    startupContext,
    suggestedQuestions,
    topicsDiscussed,
    sessionTokens,
  };
}

// =============================================================================
// Quick Analysis Hook
// =============================================================================

export interface QuickAnalysisResult {
  summary: string;
  keyPoints: string[];
  risks: string[];
  questions: string[];
}

/**
 * Hook for quick startup analysis (single-shot)
 */
export function useQuickAnalysis(options: {
  onSuccess?: (result: QuickAnalysisResult) => void;
  onError?: (error: Error) => void;
} = {}) {
  const [result, setResult] = useState<QuickAnalysisResult | null>(null);

  // Create assistant for mock mode
  const assistantRef = useRef<IDDAssistant | null>(null);
  if (!assistantRef.current) {
    assistantRef.current = createDDAssistant();
  }

  const mutation = useMutation({
    mutationFn: async (context: DDStartupContext) => {
      const analysisPrompt = 'Please provide a quick investment analysis overview of this startup, including key strengths, concerns, and questions I should investigate further.';

      // MOCK MODE: Use assistant directly
      if (mode.isMock) {
        const assistant = assistantRef.current!;

        const assistantContext: DDAssistantContext = {
          message: analysisPrompt,
          startupId: 'quick-analysis',
          investorId: 'mock-investor',
          startupContext: context,
          history: [],
        };

        const response = await assistant.chat(assistantContext);

        const analysisResult: QuickAnalysisResult = {
          summary: response.message.content.split('\n\n')[0] || response.message.content.substring(0, 500),
          keyPoints: extractBulletPoints(response.message.content, ['strength', 'positive', 'advantage']),
          risks: extractBulletPoints(response.message.content, ['risk', 'concern', 'weakness', 'challenge']),
          questions: response.suggestedQuestions || [],
        };

        return analysisResult;
      }

      // DEMO/PRODUCTION: Use edge function
      const request: DDChatRequest = {
        message: analysisPrompt,
        history: [],
        startupContext: context,
      };

      const response = await sendDDChatMessage(request);

      if (response.error || !response.data?.success || !response.data.data) {
        throw new Error(response.error || response.data?.error || 'Failed to analyze');
      }

      const content = response.data.data.message.content;

      const analysisResult: QuickAnalysisResult = {
        summary: content.split('\n\n')[0] || content.substring(0, 500),
        keyPoints: extractBulletPoints(content, ['strength', 'positive', 'advantage']),
        risks: extractBulletPoints(content, ['risk', 'concern', 'weakness', 'challenge']),
        questions: response.data.data.suggestedQuestions || [],
      };

      return analysisResult;
    },
    onSuccess: (analysisResult) => {
      setResult(analysisResult);
      options.onSuccess?.(analysisResult);
    },
    onError: (error) => {
      options.onError?.(error instanceof Error ? error : new Error(String(error)));
    },
  });

  return {
    analyze: (context: DDStartupContext) => mutation.mutate(context),
    isAnalyzing: mutation.isPending,
    result,
    error: mutation.error,
    reset: () => {
      setResult(null);
      mutation.reset();
    },
  };
}

/**
 * Extract bullet points containing certain keywords from content
 */
function extractBulletPoints(content: string, keywords: string[]): string[] {
  const lines = content.split('\n');
  const bulletPoints: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('-') || trimmed.startsWith('•') || trimmed.match(/^\d+\./)) {
      const text = trimmed.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '');
      const lowerText = text.toLowerCase();

      if (keywords.some((kw) => lowerText.includes(kw))) {
        bulletPoints.push(text);
      }
    }
  }

  return bulletPoints.slice(0, 5); // Limit to top 5
}
